/**
 * Which build this is, and which build the service is.
 *
 * ## Why a build number and a commit
 *
 * The number is monotonic, so it orders two builds without anybody having to decide what
 * kind of change each one was; the commit says exactly what went into it. Either alone is
 * half an answer — a hash does not tell you which of two deployments is newer, and a
 * count does not tell you what is in it — so the stamp is always both, written
 * `12/0a0b0c0d` and spoken "build twelve".
 *
 * ## Why two of them are shown
 *
 * A deployment is two artefacts that are replaced separately: this page, and the service
 * it signs in to. The failure worth catching is the one where only one of them was, which
 * looks exactly like everything working right up until somebody uses the part that
 * changed. Both halves are stamped from the same file in the same packaging run, so two
 * numbers that disagree is not two version schemes drifting — it is a half-finished
 * deployment, and the panel says so in as many words rather than leaving it to be spotted.
 *
 * ## Unstamped is a real answer
 *
 * `npm run dev`, and any build made outside the packaging targets, has no stamp. That is
 * not an error and it is not zero: it is a build nobody released, and saying so is more
 * use than a confident-looking number that means "we could not tell".
 */

/** A build, as it is stamped. Both halves are strings: neither is arithmetic. */
export interface BuildStamp {
  /** A whole number, as digits. */
  readonly build: string;
  /** The abbreviated commit it was cut from. */
  readonly commit: string;
}

/**
 * Recognised: digits, a slash, and an abbreviated commit.
 *
 * Deliberately strict about the shape rather than accepting whatever it is handed. The
 * two things that reach this are a build-time substitution and an HTTP response, and both
 * have ways of arriving as something else entirely — an unsubstituted placeholder, a
 * proxy's error page — which would otherwise be shown to somebody as a version number.
 */
const STAMP = /^(\d{1,9})\/([0-9a-f]{4,40})$/;

/** What the backend's `build.rs` writes when there was no stamp to bake in. */
const UNSTAMPED_COMMIT = 'unstamped';

/**
 * Read a stamp written `12/0a0b0c0d`.
 *
 * @returns The stamp, or `undefined` for anything that is not one — including a build
 *   that was never stamped, which says `0/unstamped` and is not a release.
 */
export function parseBuildStamp(raw: string | undefined | null): BuildStamp | undefined {
  const match = STAMP.exec((raw ?? '').trim());
  if (match === null) return undefined;

  const [, build, commit] = match;
  if (build === undefined || commit === undefined || Number(build) === 0) {
    return undefined;
  }

  return { build, commit };
}

/**
 * Read a stamp that arrived as two fields rather than as one string.
 *
 * Which is how the service sends it: a client that had to parse a joined format would
 * have to be kept in step with whoever writes it, for no gain.
 */
export function buildStampFrom(build: unknown, commit: unknown): BuildStamp | undefined {
  if (typeof build !== 'string' || typeof commit !== 'string') return undefined;
  if (commit.trim() === UNSTAMPED_COMMIT) return undefined;
  return parseBuildStamp(`${build.trim()}/${commit.trim()}`);
}

/** The stamp as one string, for a log line or a bug report. */
export function formatBuildStamp(stamp: BuildStamp): string {
  return `${stamp.build}/${stamp.commit}`;
}

/**
 * The lines the About panel shows, in order.
 *
 * A pure function of the two facts, so that every rule about what is said when — and in
 * particular when the mismatch is called out — is one a test can state outright.
 *
 * @param page This build, or `undefined` if it was never stamped.
 * @param service The service's build, or `undefined` if there is no service configured,
 *   it could not be reached, or it is itself unstamped. All three come to the same thing
 *   here: nothing can be said about it, so nothing is.
 */
export function describeBuilds(
  page: BuildStamp | undefined,
  service: BuildStamp | undefined,
): readonly string[] {
  const lines = [
    page === undefined ? 'Unstamped build' : `Build ${formatBuildStamp(page)}`,
  ];

  if (service !== undefined) {
    lines.push(`Service build ${formatBuildStamp(service)}`);
  }

  // Only worth saying when both are known and they disagree. Comparing the build numbers
  // rather than the commits: a mismatched commit at the same build number would mean
  // somebody rebuilt without stamping, which is a different problem and not one this
  // sentence would help with.
  if (page !== undefined && service !== undefined && page.build !== service.build) {
    lines.push('The page and the service are from different builds.');
  }

  return lines;
}
